import { createAction } from '@reduxjs/toolkit';
import { ofType } from 'redux-observable';
import { from, of } from 'rxjs';
import { catchError, exhaustMap, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { datadogRum } from '@datadog/browser-rum';
import { requestETASuccess } from '../../../redux/actions/eta.actions';
import attachmentApiService from '../../../services/attachmentApi.service';
import PCSFormService from '../../../services/pcs.service';

export const submitPCSForm = createAction('[PhysicianCertificationStatement] Submit PCS Form');
export const submitPCSFormSuccess = createAction('[PhysicianCertificationStatement] Submit PCS Form Success');
export const submitPCSFormFailure = createAction('[PhysicianCertificationStatement] Submit PCS Form Failure');
export const createPCSAttachment = createAction('[PhysicianCertificationStatement] Create PCS Attachment');
export const createPCSAttachmentSuccess = createAction(
  '[PhysicianCertificationStatement] Create PCS Attachment Success',
);
export const createPCSAttachmentFailure = createAction(
  '[PhysicianCertificationStatement] Create PCS Attachment Failure',
);

export const submitPCSFormEpic = (action$, store) =>
  action$.pipe(
    ofType(submitPCSForm.type),
    withLatestFrom(store),
    switchMap(([action, state]) => {
      datadogRum.addAction('pcs_form_submission_started', {
        formDataKeys: Object.keys(state.physicianCertificationStatement.formData || {}),
        requestId: state.requests?.request?.id,
      });
      console.log('[PCS Epic] Starting PCS form submission', {
        formData: state.physicianCertificationStatement.formData,
        requestId: state.requests?.request?.id,
      });

      return from(PCSFormService.submitPCSForm(state.physicianCertificationStatement.formData)).pipe(
        map(blob => {
          if (!(blob instanceof Blob)) {
            const error = 'Invalid response: expected a Blob';
            datadogRum.addError(new Error(error), {
              context: 'pcs_form_submission',
              responseType: typeof blob,
              responseValue: blob,
            });
            console.error('[PCS Epic] Invalid response from PCS service', {
              responseType: typeof blob,
              responseValue: blob,
            });
            throw new Error(error);
          }

          datadogRum.addAction('pcs_form_submission_success', {
            blobSize: blob.size,
            blobType: blob.type,
            requestId: state.requests?.request?.id,
          });
          console.log('[PCS Epic] PCS form submission successful', {
            blobSize: blob.size,
            blobType: blob.type,
            requestId: state.requests?.request?.id,
          });

          return submitPCSFormSuccess(blob);
        }),
        catchError(error => {
          datadogRum.addError(error, {
            context: 'pcs_form_submission_inner_catch',
            formDataKeys: Object.keys(state.physicianCertificationStatement.formData || {}),
            requestId: state.requests?.request?.id,
            httpStatus: error.status,
            httpStatusText: error.statusText,
            requestUrl: error.url,
            errorDetails: error.details,
          });
          console.error('[PCS Epic] PCS form submission failed (inner catch)', {
            error: error,
            errorMessage: error.message,
            errorStack: error.stack,
            httpStatus: error.status,
            httpStatusText: error.statusText,
            requestUrl: error.url,
            errorDetails: error.details,
            requestId: state.requests?.request?.id,
          });
          return of(submitPCSFormFailure(error.message));
        }),
      );
    }),
    catchError(error => {
      datadogRum.addError(error, {
        context: 'pcs_form_submission_outer_catch',
        httpStatus: error.status,
        httpStatusText: error.statusText,
        requestUrl: error.url,
        errorDetails: error.details,
      });
      console.error('[PCS Epic] PCS form submission failed (outer catch)', {
        error: error,
        errorMessage: error.message,
        errorStack: error.stack,
        httpStatus: error.status,
        httpStatusText: error.statusText,
        requestUrl: error.url,
        errorDetails: error.details,
      });
      return of(submitPCSFormFailure(error.message));
    }),
  );

export const createPCSAttachmentEpic = (action$, store) =>
  action$.pipe(
    ofType(requestETASuccess.type, submitPCSFormSuccess.type),
    withLatestFrom(store),
    map(([, state]) => {
      const { attachmentCreated, blob } = state.physicianCertificationStatement;
      const requestId = state.requests?.request?.id;
      const attachmentType = state.patientDocuments.attachmentTypes?.find(type => type.name === 'PCS');

      return { attachmentCreated, attachmentType, blob, requestId };
    }),
    filter(({ attachmentCreated, attachmentType, blob, requestId }) =>
      Boolean(blob && requestId && attachmentType && !attachmentCreated),
    ),
    exhaustMap(({ attachmentType, blob, requestId }) => {
      return from(attachmentApiService.createAttachmentFromBlob(requestId, blob, attachmentType.id, 'PCS.pdf')).pipe(
        map(response => createPCSAttachmentSuccess(response)),
        catchError(error => {
          return of(createPCSAttachmentFailure(error.message));
        }),
      );
    }),
  );

export const reducer = {
  [submitPCSForm]: (state, payload) => ({
    ...state,
    formData: payload,
    blob: null,
    attachmentCreated: false,
    attachmentError: null,
    submitInProgress: true,
    submitError: null,
  }),
  [submitPCSFormSuccess]: (state, payload) => ({
    ...state,
    blob: payload,
    submitSuccess: true,
    submitError: null,
    submitInProgress: false,
  }),
  [submitPCSFormFailure]: (state, payload) => ({
    ...state,
    submitSuccess: false,
    submitError: payload,
    submitInProgress: false,
  }),
  [createPCSAttachmentSuccess]: (state, payload) => ({
    ...state,
    attachmentCreated: true,
    attachmentError: null,
  }),
  [createPCSAttachmentFailure]: (state, payload) => ({
    ...state,
    attachmentCreated: false,
    attachmentError: payload,
  }),
};
